import React from 'react'
import {Avatar, Box, Typography, useTheme} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import {useAppSelector} from 'src/hooks'

interface UserChipProps {
  expanded: boolean
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  showChevron?: boolean
  active?: boolean
}

const UserChip = ({expanded, onClick, showChevron, active}: UserChipProps): JSX.Element => {
  const theme = useTheme()
  const firstName = useAppSelector(state => state.userReducer.data.firstName)
  const lastName = useAppSelector(state => state.userReducer.data.lastName)
  const title = useAppSelector(state => state.userReducer.data.title)

  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()
  const displayName = [title, firstName, lastName].filter(Boolean).join(' ')

  const interactive = typeof onClick === 'function'

  return (
    <Box
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.(e as unknown as React.MouseEvent<HTMLElement>)
        }
      } : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: expanded ? 1.5 : 0,
        px: expanded ? 1.5 : 1,
        py: 0.75,
        borderRadius: 1,
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
        justifyContent: expanded ? 'flex-start' : 'center',
        backgroundColor: active ? 'action.hover' : 'transparent',
        transition: 'background-color 0.15s ease',
        '&:hover': interactive ? {backgroundColor: 'action.hover'} : undefined,
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontSize: 13,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {initials || '?'}
      </Avatar>
      {expanded && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
        </Typography>
      )}
      {expanded && showChevron && (
        <KeyboardArrowDownIcon
          fontSize="small"
          sx={{
            color: theme.palette.text.secondary,
            transition: 'transform 0.15s ease',
            transform: active ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      )}
    </Box>
  )
}

export default UserChip
